import type { LucideIcon } from "lucide-react";
import { Icon } from "../Icon/Icon.js";
import styles from "./ActionMenu.module.scss";

/** One entry in an action menu — `SplitButton`'s caret list and `MenuButton`'s overflow are the same list. */
export interface ActionMenuItem {
  /** Menu label — plain text, so it is what the type-ahead and AT both read. */
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
}

/** Which way the popup opens. `above` is for a control pinned to the bottom of its container. */
export type MenuPlacement = "below" | "above";

/**
 * Which EDGE the popup lines up with. `end` (the default) hangs it off the trigger's right edge, growing
 * leftward — right for a control sitting at the right of its row, which is where every menu in the app was
 * when this was written. `start` is for a trigger near the LEFT edge of a narrow container, where the
 * default would grow the panel straight off the side and a scrolling ancestor would clip it away.
 */
export type MenuAlign = "start" | "end";

/**
 * The popup itself. Absolutely positioned against the trigger's wrapper, so a caller must be the one to
 * establish the containing block (`SplitButton`'s `.split`, `MenuButton`'s `.root`).
 *
 * Selecting an item closes the menu BEFORE running the action: several actions open a dialog of their own,
 * and closing after would leave the menu painted underneath it.
 */
export function ActionMenu({
  id,
  items,
  placement = "below",
  align = "end",
  onSelected,
}: {
  id: string;
  items: ActionMenuItem[];
  placement?: MenuPlacement;
  align?: MenuAlign;
  /** Called as soon as an item is chosen, so the trigger can close itself. */
  onSelected: () => void;
}) {
  return (
    <div className={styles.menu} data-placement={placement} data-align={align} role="menu" id={id}>
      {items.map((a) => (
        <button
          key={a.label}
          type="button"
          role="menuitem"
          className={styles.item}
          disabled={a.disabled}
          onClick={() => {
            onSelected();
            a.onSelect();
          }}
        >
          {a.icon && <Icon icon={a.icon} size={16} />}
          {a.label}
        </button>
      ))}
    </div>
  );
}
